import React from 'react';

import { PatientProps, usePatientContext } from '../../../../contexts/PatientContext';
import { fetchValidateExecutiveCheckup } from '../../../../modules/validateRules';
import { verifyEligibility } from '../../../../modules/eligibility';
import { fetchCatalogService } from '../../../../modules/products';
import { Page, useExecutiveCheckupRouter } from '../../router/context';

import { EligibilityPlanProps, usePaymentContext } from '../../../../contexts/PaymentContext';

import { makePatientRuleByPatientProps } from '../../../../models/Patient';
import { PAYMENT_TYPES, PaymentValidationRule } from '../../../../models/PaymentRule';

import { ReturnsAlreadyScheduledModal } from '../../components/ReturnsAlreadyScheduledModal';
import { UnavailableReturnsModal } from '../../components/UnavailableReturnsModal';
import SystemUnavailable from '../../../../components/SystemUnavailable';
import { LoadingOverlay } from '../../../../components';

import { PatientFormTemplate } from '../../../../templates/PatientFormTemplate';
import { useServiceContext } from '../../../../contexts/ServiceContext';
import { ServiceItemProps } from '../../../../models/Service';
import { PAYMENT_TYPE } from '../../../../utils/constants';
import { useScheduleContext } from '../../../../contexts/ScheduleContext';
import { ScheduleItemPropsBuilder } from '../../../../models/Schedule';
import { ProductItemProps } from '../../../../models/Product';
import ModalNoEligibility from '../../components/ModalNoEligibility';
import { LateAppointmentModal } from '../../components/LateAppointmentModal';
import { LateAppointmentError } from '../../../../errors/LateAppointmentError';
import { UnavailableReturnsError } from '../../../../errors/UnavailableReturnsError';
import { FirstAppointmentUnavailableError } from '../../../../errors/FirstAppointmentUnavailableError';
import { SystemUnavailableError } from '../../../../errors';
import { FirstAppointmentUnderTimeLimit } from '../../../../errors/FirstAppointmentUnderTimeLimit';
import { FirstAppointmentUnderTimeLimitModal } from '../../components/FirstAppointmentUnderTimeLimitModal';

import { useScheduledItems } from '../../store/ScheduledItems';
import { useMainRouter } from '../../../../Router/pageComponents';

export function PatientFormPage() {
  const { setEligibilityPlans } = usePaymentContext();
  const { patientSelected, addPatientList } = usePatientContext();
  const { changeSchedule, selectedItem: schedule } = useScheduleContext();
  const { selectedService: service, setSeletedService: setSelectedService } = useServiceContext();
  const router = useExecutiveCheckupRouter();

  const { setScheduledItems } = useScheduledItems();

  const [loading, setLoading] = React.useState(false);
  const [isModalNoEligibility, setIsModalNoEligibility] = React.useState(false);
  const [isModalLateAppointmentVisible, setIsModalLateAppointmentVisible] = React.useState(false);
  const [isModalUnavailableReturnsVisible, setIsModalUnavailableReturnsVisible] = React.useState(false);
  const [isModalSystemUnavailableVisible, setIsModalSystemUnavailableVisible] = React.useState(false);
  const [isAppointmentUnderTimeLimit, setIsAppointmentUnderTimeLimit] = React.useState(false);
  const [isModalReturnsAlreadyScheduledVisible, setIsModalReturnsAlreadyScheduledVisible] = React.useState(false);

  function makePaymentValidationByEligibilityPlan(eligibilityPlan: EligibilityPlanProps) {
    return {
      type: PAYMENT_TYPES.B2B,
      additional_info: {
        plan_code: eligibilityPlan.cod_plano
      }
    } as PaymentValidationRule;
  }

  async function validateEligibilityPlans({
    patient,
    serviceId
  }: {
    patient: PatientProps;
    serviceId: string;
  }): Promise<EligibilityPlanProps[]> {
    const { data, error, message } = await verifyEligibility({
      document: patient.document,
      documentType: patient.documentType,
      itemId: serviceId,
      eligibilityType: 'SERVICE',
      birthDate: patient.birthDate
    });

    if (data.length === 1) {
      const [plan] = data;
      if (!plan.cod_plano && !plan.cod_convenio) {
        setIsModalNoEligibility(true);
        return [];
      }
    }

    if (data.length === 0 || message === 'Plano não encontrado') setIsModalNoEligibility(true);
    if (data.length === 0 || message === 'Data de aniversário é inválida') setIsModalNoEligibility(true);
    else if (error) setIsModalSystemUnavailableVisible(true);

    setEligibilityPlans(data);

    return data;
  }

  function createSchedule(props: {
    patientName: string;
    patientId: string;
    product?: ProductItemProps;
    eligibilityPlan?: EligibilityPlanProps;
  }) {
    const { eligibilityPlan, patientId, patientName, product } = props;

    const scheduleFactory = ScheduleItemPropsBuilder();
    scheduleFactory.setPatientName(patientName);
    scheduleFactory.setPatient(patientId);

    if (eligibilityPlan?.cod_plano)
      scheduleFactory.setPayment({
        type: PAYMENT_TYPE.B2B,
        eligibility: {
          idInsurance: eligibilityPlan.id_convenio,
          idCustomer: eligibilityPlan.id_cliente,
          codInsurance: eligibilityPlan.cod_convenio,
          codPlan: eligibilityPlan.cod_plano,
          codOffice: eligibilityPlan.cod_escritorio,
          plan: eligibilityPlan.nome,
          image: eligibilityPlan.image
        }
      });

    if (product?.id) scheduleFactory.setProduct({ ...product, name: productName(product.name) });

    changeSchedule({ ...schedule, ...scheduleFactory.build() });
  }

  function productName(name: string) {
    return (name.split(' - ')[1] || name).trim();
  }

  const handleError = (error: Error) => {
    if (error instanceof LateAppointmentError) setIsModalLateAppointmentVisible(true);
    if (error instanceof UnavailableReturnsError) setIsModalUnavailableReturnsVisible(true);
    if (error instanceof FirstAppointmentUnavailableError) setIsModalNoEligibility(true);
    if (error instanceof SystemUnavailableError) setIsModalSystemUnavailableVisible(true);
    if (error instanceof FirstAppointmentUnderTimeLimit) setIsAppointmentUnderTimeLimit(true);
  };

  async function validateRulesCheckup(props: {
    service: ServiceItemProps;
    patient: PatientProps;
    eligibilityPlan: EligibilityPlanProps;
    createScheduleParams: any;
  }): Promise<{ isFirstAppointment: boolean; firstAppointmentDate: string } | undefined> {
    const { eligibilityPlan, patient, service: checkup } = props;

    try {
      const response = await fetchValidateExecutiveCheckup({
        service: checkup,
        patient: makePatientRuleByPatientProps(patient),
        payment: makePaymentValidationByEligibilityPlan(eligibilityPlan)
      });

      const { isFirstAppointment, service, scheduled, firstAppointmentDate } = response;

      if (service?.products?.length === 0) setIsModalReturnsAlreadyScheduledVisible(true);
      else {
        const products = service.products.map((product: ProductItemProps) => {
          return {
            ...product,
            name: productName(product.name)
          };
        });

        if (service?.products.length === 1) {
          const [product] = service.products;

          props.createScheduleParams.product = product;
        }

        setSelectedService({
          ...service,
          products
        });

        if (scheduled?.length) setScheduledItems(scheduled);

        return { isFirstAppointment, firstAppointmentDate };
      }
    } catch (error: any) {
      console.error('Error trying validate service', error);

      handleError(error);
    }
  }

  const handlePatient = async (patient: PatientProps) => {
    setLoading(true);

    const eligibilityPlans = await validateEligibilityPlans({ patient, serviceId: service.id });

    const createScheduleParams = {
      eligibilityPlan: {} as EligibilityPlanProps,
      patientId: '',
      patientName: patient.name || '',
      product: {} as ProductItemProps
    };

    const { idPatient } = addPatientList(patient);
    createScheduleParams.patientId = idPatient || '';

    if (eligibilityPlans?.length > 1) router.navigate(Page.PAYMENT);

    if (eligibilityPlans?.length === 1) {
      const [eligibilityPlan] = eligibilityPlans;

      createScheduleParams.eligibilityPlan = eligibilityPlan;

      const res = await fetchCatalogService(service.id);

      const validate = await validateRulesCheckup({
        service: res.data,
        eligibilityPlan,
        patient,
        createScheduleParams
      });

      if (validate) {
        const { firstAppointmentDate, isFirstAppointment } = validate;

        if (isFirstAppointment === true)
          router.navigate(Page.COMPLEMENTARY_EXAMS, {
            isFirstAppointment,
            firstAppointmentDate: Number(firstAppointmentDate) || 0
          });
        if (isFirstAppointment === false) router.navigate(Page.INFO, { isFirstAppointment, firstAppointmentDate });
      }
    }

    createSchedule(createScheduleParams);

    setLoading(false);
  };

  const { goBack: mainRouterGoBack } = useMainRouter();

  function handleGoBack() {
    if (router.history.length === 1) return mainRouterGoBack();
    else router.goBack();
  }

  return (
    <>
      <PatientFormTemplate patient={patientSelected} handlePatientSubmit={handlePatient} goBack={handleGoBack} />

      <SystemUnavailable
        visible={isModalSystemUnavailableVisible}
        onClose={() => setIsModalSystemUnavailableVisible(false)}
      />
      <UnavailableReturnsModal
        visible={isModalUnavailableReturnsVisible}
        onClose={() => setIsModalUnavailableReturnsVisible(false)}
      />
      <ReturnsAlreadyScheduledModal
        visible={isModalReturnsAlreadyScheduledVisible}
        onClose={() => setIsModalReturnsAlreadyScheduledVisible(false)}
      />
      <ModalNoEligibility visible={isModalNoEligibility} onClose={() => setIsModalNoEligibility(false)} />

      <LateAppointmentModal
        visible={isModalLateAppointmentVisible}
        onClose={() => setIsModalLateAppointmentVisible(false)}
      />

      <FirstAppointmentUnderTimeLimitModal
        visible={isAppointmentUnderTimeLimit}
        onClose={() => setIsAppointmentUnderTimeLimit(false)}
      />

      <LoadingOverlay loading={loading} />
    </>
  );
}
